'use client';

import { ArrowRight, Loader2, Square, X } from 'lucide-react';
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import Attach from '../MessageInputActions/Attach';
import { File, Message } from '@/type/chat';
import SeacrhRange from '../MessageInputActions/SearchRange';
import DeepSearchSwitch from '../MessageInputActions/DeepSearchSwitch';
import useSWRMutation from 'swr/mutation';
import useSWR from 'swr';
import { cn } from '@/lib/utils';
import { DictationIcon } from '../Icons';
import DictationMode from '../SearchInput/DictationMode';
import SuggestionDropdown from '../SearchInput/SuggestionDropdown';
import { useTranslation } from '@/lib/i18n';
import SearchLimitReached from './SearchLimitReached';
import ResearchMenuIcon from '../Icons/ResearchMenuIcon';
import AlfaGptMenuIcon from '../Icons/AlfaGptMenuIcon';
import { AgentMode, ChatMode, useChatMode } from '@/contexts/ChatModeContext';
import AgentVersion from '../MessageInputActions/AgentVersion';
import { useEventEmitter } from '@/hooks/useEventEmitter';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import StrategyTypes from '../MessageInputActions/StrategyTypes';
import StrategyQueryPreset from '../MessageInputActions/StrategyQueryPreset';
import { Stratege } from '../Workspace/StrategyLab/strategy-config';
import PredicIcon from '../predic/ui/PredicIcon';
import { usePathname } from 'next/navigation';
import { logEvent } from '@/lib/firebase';
import { trackXAdsEvent } from '@/lib/xAds';

// 声明 Web Speech API 类型
interface IWindow extends Window {
  webkitSpeechRecognition: any;
  SpeechRecognition: any;
}

const EmptyChatMessageInput = ({
  loading,
  sendMessage,
  handleEditMessage,
  allMessages,
  focusMode,
  setFocusMode,
  optimizationMode,
  setOptimizationMode,
  fileIds,
  setFileIds,
  files,
  setFiles,
  lastMessage,
  onAbort,
  currentTaskId,
  isCreating = false,
  isSmall = false,
  floating = false,
  inChat = false,
  isStrategy = false,
  showProductIcon = true,
  colorfulInput = true,
  preContext = '',
  onMessageSend, // ✅ 新增：消息发送时的回调
  strategyType,
  setStrategyType,
  suggestions,
}: {
  loading?: boolean;
  sendMessage: (message: string, messageid?: string, parentMessage?: Message) => Promise<void>;
  handleEditMessage?: (
    messageId: string,
    newContent: string,
    createBranch: boolean,
  ) => Promise<void>;
  allMessages?: Message[];
  focusMode?: string[];
  setFocusMode?: (mode: string[]) => void;
  optimizationMode?: string;
  setOptimizationMode?: (mode: string) => void;
  fileIds?: string[];
  setFileIds?: (fileIds: string[]) => void;
  files?: File[];
  setFiles?: (files: File[]) => void;
  lastMessage?: Message;
  onAbort?: () => void;
  currentTaskId?: string | null;
  isCreating?: boolean;
  isSmall?: boolean;
  floating?: boolean;
  inChat?: boolean;
  showProductIcon?: boolean;
  preContext?: string;
  isStrategy?: boolean;
  colorfulInput?: boolean;
  onMessageSend?: () => void; // ✅ 新增：消息发送时的回调类型
  strategyType?: Stratege;
  setStrategyType?: (strategy?: Stratege) => void;
  suggestions?: string[];
}) => {
  const pathname = usePathname();
  const { data: apiAccessToken } = useSWR('apiAccessToken');
  const { trigger: setLoginDialogOpen } = useSWRMutation('loginDialogOpen');
  const { trigger: setPreChatContext } = useSWRMutation('preChatContext');
  const { openModal } = useSubscription();
  const { t, currentLanguage } = useTranslation();

  const requestSuggestion = !suggestions?.length || !inChat;
  const sugUrl = !pathname.startsWith('/predic')
    ? `api:/news?language=${currentLanguage || 'en'}`
    : `api:/predic/traces?language=${currentLanguage || 'en'}`;

  // const { data: newsData } = useSWR(requestSuggestion ? sugUrl : null, {
  const { data: newsData } = useSWR(null, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
  });

  const emitter = useEventEmitter();
  const { mode, isWorkspace, agent } = useChatMode();
  const isResearch = mode === 'research';
  const [message, setMessage] = useState('');
  const [quotedText, setQuotedText] = useState<string | undefined>(undefined); // 新增：引用文本状态
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [deepSearchEnabled, setDeepSearchEnabled] = useState(!!isResearch);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showExpand, setShowExpand] = useState(false);

  const isExpanded = message.includes('\n') || showExpand;

  // 听写相关状态
  const [isDictating, setIsDictating] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const recognitionRef = useRef<any>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number>(0);
  const barsConfigRef = useRef<Array<{ duration: number; delay: number }>>([]);
  const isMouseDownRef = useRef(false);

  //search limit
  const { showError } = useSnackbar();
  const { searchAllowed, researchAllowed } = useSubscription();
  const searchEnable = mode === 'alfa' ? searchAllowed : researchAllowed;

  const onClearQuotedText = () => {
    setQuotedText(undefined);
    emitter.emit('message-preset', { action: 'quote', content: '' });
  };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    console.log('[EmptyChatMessageInput onSubmit] 被调用了!', {
      message,
      quotedText,
      loading,
      searchEnable,
      apiAccessToken,
    });

    if (!apiAccessToken) {
      setLoginDialogOpen(true);
      e.preventDefault();
      return;
    }
    if (loading && lastMessage) {
      e.preventDefault();
      onAbort?.();
      return;
    }
    //
    if (!searchEnable) {
      showError(t('Search limit reached') + ', ' + t('Upgrade to keep searching without limits'));
      // if (mode === 'alfa') {
      //   setSearchLimitShow(true);
      // } else {
      //   setReSearchLimitShow(true);
      // }
      openModal();
      e.preventDefault();
      return;
    }
    // 如果没有输入内容
    if (!message.trim()) {
      console.log(
        '[EmptyChatMessageInput] 消息为空，isSmall:',
        isSmall,
        'hasQuery:',
        !!allQueries[placeholderIndex],
      );
      // 当 isSmall=false 且有 placeholder query 时，使用 placeholder 进行搜索
      if (!isSmall && allQueries[placeholderIndex]) {
        e.preventDefault();
        if (preContext && preContext !== '') {
          setPreChatContext(preContext);
        }
        sendMessage(allQueries[placeholderIndex]);
        return;
      }
      // 否则不执行任何操作
      e.preventDefault(); // ✅ 添加 preventDefault
      return;
    }
    console.log('[EmptyChatMessageInput] 准备调用 sendMessage，message:', message);
    console.log('[EmptyChatMessageInput] sendMessage 类型:', typeof sendMessage, sendMessage);
    e.preventDefault();

    // ✅ 检查 lastMessage 是否是没有回答的 user 消息
    if (inChat && handleEditMessage && allMessages && lastMessage && lastMessage.role === 'user') {
      // 检查是否有对应的 assistant 回复
      const hasAssistantResponse = allMessages.some(
        (m) => m.role === 'assistant' && m.parentMessageId === lastMessage.messageId,
      );

      if (!hasAssistantResponse) {
        console.log('[EmptyChatMessageInput] 检测到上一条消息没有回复，调用 handleEditMessage');
        // 调用编辑方法，创建分支
        await handleEditMessage(lastMessage.messageId, message, true);
        setMessage('');
        setShowExpand(false);
        onClearQuotedText();
        onMessageSend?.();
        return;
      }
    }

    // 正常的追问流程
    if (preContext && preContext !== '') {
      setPreChatContext(preContext);
    }
    console.log('[EmptyChatMessageInput] 即将调用 sendMessage');
    // ✅ 直接传递 message，quotedText 已通过 state 传递
    logEvent('user_search_chat', {
      question_length: message.length,
    });
    trackXAdsEvent('user_search_chat', {
      question_length: message.length,
    });
    sendMessage(message, undefined, lastMessage);
    console.log('[EmptyChatMessageInput] sendMessage 已调用');
    setMessage('');
    setShowExpand(false);
    setQuotedText(undefined); // 清除引用文本

    // ✅ 调用滚动回调
    onMessageSend?.();
  }

  // 获取所有的query数据
  const allQueries = React.useMemo(() => {
    if (!newsData?.data?.news && !suggestions?.length) return [];

    if (suggestions?.length) {
      return suggestions;
    }

    const queries: string[] = [];
    newsData.data.news.forEach((item: any) => {
      if (item.query) {
        try {
          // 如果query是字符串，尝试解析为JSON数组
          const parsedQuery = typeof item.query === 'string' ? JSON.parse(item.query) : item.query;

          if (Array.isArray(parsedQuery)) {
            queries.push(...parsedQuery);
          }
        } catch (e) {
          // 如果解析失败，直接使用原始字符串
          if (typeof item.query === 'string') {
            queries.push(item.query);
          }
        }
      }
    });
    return queries;
  }, [newsData, suggestions]);

  // 每2分钟切换placeholder
  useEffect(() => {
    if (allQueries.length === 0 || floating) return;

    const interval = setInterval(
      () => {
        setPlaceholderIndex((prevIndex) => (prevIndex + 1) % allQueries.length);
      },
      2 * 60 * 1000,
    ); // 2分钟

    return () => clearInterval(interval);
  }, [allQueries?.length, floating]);

  // 初始化语音识别
  const initRecognition = useCallback(() => {
    const win = window as unknown as IWindow;
    const SpeechRecognition = win.SpeechRecognition || win.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error('浏览器不支持语音识别');
      return null;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'zh-CN';

    recognition.onresult = (event: any) => {
      let interimText = '';
      let finalText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        } else {
          interimText += result[0].transcript;
        }
      }

      if (finalText) {
        setTranscript((prev) => prev + finalText);
        setInterimTranscript('');
      } else {
        setInterimTranscript(interimText);
      }
    };

    recognition.onerror = (event: any) => {
      console.error('语音识别错误:', event.error);
      if (event.error === 'no-speech') {
        // 静音时不自动重启，等用户操作
      } else if (event.error === 'aborted' || event.error === 'not-allowed') {
        // 用户取消或没有权限
        // 错误时设置标志，让调用者处理
        setIsDictating(false);
        setIsListening(false);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      // 不自动重启，让用户控制
    };

    return recognition;
  }, []);

  // 初始化音频分析
  const initAudioAnalyser = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;

      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      // 开始动画
      const updateLevel = () => {
        if (analyserRef.current) {
          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          setAudioLevel(average / 255);
        }
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch (error) {
      console.error('获取麦克风权限失败:', error);
      setIsDictating(false);
      setIsListening(false);
    }
  }, []);

  // 清理音频资源
  const cleanupAudio = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setAudioLevel(0);
  }, []);

  // 开始听写
  const startDictation = useCallback(async () => {
    setIsDictating(true);
    setTranscript('');
    setInterimTranscript('');

    const recognition = initRecognition();
    if (recognition) {
      recognitionRef.current = recognition;
      try {
        recognition.start();
        setIsListening(true);
      } catch (e) {
        console.error('启动识别失败:', e);
        setIsDictating(false);
        setIsListening(false);
        return;
      }
    }

    await initAudioAnalyser();
  }, [initRecognition, initAudioAnalyser]);

  // 停止听写
  const stopDictation = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.error('停止识别失败:', e);
      }
      recognitionRef.current = null;
    }

    cleanupAudio();

    // 将识别的文字回填到输入框
    const finalText = transcript + interimTranscript;
    if (finalText.trim()) {
      setMessage((prevMessage) => prevMessage + finalText);
    }

    setIsDictating(false);
    setIsListening(false);
    setTranscript('');
    setInterimTranscript('');

    // 聚焦输入框
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [transcript, interimTranscript, cleanupAudio]);

  const disabled = useMemo(() => {
    if (isCreating) {
      return true;
    }

    if (inChat) {
      return !message.trim() && !loading;
    }

    if (floating) {
      return !message.trim() && !allQueries[placeholderIndex];
    }

    return !message.trim();
  }, [isCreating, message, inChat, floating, allQueries, placeholderIndex]);

  // 初始化波形条形配置
  useEffect(() => {
    if (isDictating && barsConfigRef.current.length === 0) {
      const barCount = 30;
      const configs = [];
      for (let i = 0; i < barCount; i++) {
        configs.push({
          duration: 0.8 + Math.random() * 0.4,
          delay: i * 0.02,
        });
      }
      barsConfigRef.current = configs;
    } else if (!isDictating) {
      barsConfigRef.current = [];
    }
  }, [isDictating]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement;

      const isInputFocused =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.hasAttribute('contenteditable');

      if (e.key === '/' && !isInputFocused) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // inputRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // 处理建议选择 - 直接搜索
  const handleSuggestionSelect = useCallback(
    (suggestion: string) => {
      setShowSuggestions(false);
      // 如果有预置上下文，设置它
      if (preContext && preContext !== '') {
        setPreChatContext(preContext);
      }
      // 直接发送消息进行搜索
      sendMessage(suggestion);
    },
    [preContext, setPreChatContext, sendMessage],
  );

  // 处理输入框聚焦和失焦
  const handleInputFocus = useCallback(() => {
    // 只有在鼠标按下（手动聚焦）且 isSmall=false 且输入为空时显示
    if (!isSmall && !message.trim() && isMouseDownRef.current && allQueries.length) {
      setShowSuggestions(true);
    }
    // 重置鼠标按下标记
    isMouseDownRef.current = false;
  }, [isSmall, message]);

  const handleInputBlur = useCallback(() => {
    // 延迟隐藏，以便点击建议时能触发
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  }, []);

  // 处理鼠标按下事件
  const handleMouseDown = useCallback(() => {
    isMouseDownRef.current = true;
    // 如果输入框已经聚焦，直接显示建议
    if (
      document.activeElement === inputRef.current &&
      !isSmall &&
      !message.trim() &&
      allQueries?.length
    ) {
      setShowSuggestions(true);
    }
  }, [isSmall, message, allQueries?.length]);

  // 清理资源
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.error('停止识别失败:', e);
        }
      }
      cleanupAudio();
    };
  }, [cleanupAudio]);

  useEffect(() => {
    const action = (payload: { action: string; content: string }) => {
      if (payload.action === 'quote') {
        // 将选择的文本设置为引用文本
        setQuotedText(payload.content);
      } else if (payload.action === 'preset') {
        setMessage(payload.content);
      }
    };
    emitter.on('message-preset', action);
    return () => {
      emitter.off('message-preset', action);
    };
  }, [emitter]);

  return (
    <div className="flex flex-col gap-2">
      {/* {isSmall && <SearchLimitReached />} */}
      {mode === 'strategy' && localStorage.getItem('__workspace__') && (
        <StrategyQueryPreset onSelect={setMessage} strategyType={strategyType as Stratege} />
      )}
      <form
        onSubmit={onSubmit}
        onKeyDown={(e) => {
          if (e.keyCode == 13 && !e.shiftKey) {
            onSubmit(e);
          }
        }}
        className="w-full"
      >
        <div
          className={cn(
            'relative z-10 w-full h-full rounded-2xl',
            floating ? 'rounded-full' : 'bg-[#121212]',
            !isExpanded ? 'rounded-full' : '',
          )}
        >
          <div
            className={cn(
              'absolute inset-0 dark:bg-black/20 bg-white/20 backdrop-blur-md z-0',
              (floating || inChat) && !isExpanded ? 'rounded-full' : 'rounded-2xl',
              !isExpanded ? 'rounded-full' : '',
            )}
          />
          <div
            className={cn(
              'absolute inset-0 z-10 pointer-events-none blur-bg',
              showSuggestions && !floating ? 'rounded-t-2xl px-px pt-px' : 'rounded-2xl p-px',
              (floating || inChat) && !isExpanded && 'rounded-full',
              !isExpanded ? 'rounded-full' : '',
            )}
            // style={
            //   colorfulInput
            //     ? {
            //         background: isResearch
            //           ? 'linear-gradient(to right, #67FFFF, #9453FF)'
            //           : 'linear-gradient(89.96deg, #FF5000 0.03%, #FFBE49 23.46%, #FFF6A6 41.54%, #A269FF 57.3%, #2C6AF9 78.6%, #3DE0A3 100%)',
            //       }
            //     : { backgroundColor: '#1E1E1E' }
            // }
            style={{
              background:
                'linear-gradient(89.96deg, #FF5000 0.03%, #FFBE49 23.46%, #FFF6A6 41.54%, #A269FF 57.3%, #2C6AF9 78.6%, #3DE0A3 100%)',
            }}
          />

          <div
            className={cn(
              'relative flex flex-col p-3 w-full transition-all duration-300',
              showSuggestions && !floating ? 'rounded-t-[24px]' : 'rounded-[24px]',
              isDictating && 'bg-bg-input',
              {
                'rounded-full': inChat || floating,
              },
            )}
          >
            {/* 建议下拉框 - 在 isSmall=false 时显示在下方 */}
            {allQueries?.length > 0 && (
              <SuggestionDropdown
                suggestions={allQueries}
                onSelect={handleSuggestionSelect}
                visible={showSuggestions}
                position={floating ? 'above' : 'below'}
                isResearch={isResearch}
              />
            )}

            {/* 引用文本区域 - 当有引用文本时显示 */}
            {quotedText && (
              <div className="px-3 py-2 mb-3 pb-3 border-b dark:border-[#1E1E1E] border-[#EFEFEF] flex items-start gap-2">
                <p className="flex-1 text-[14px] text-text-tertiary leading-[18px] line-clamp-2">
                  {quotedText}
                </p>
                <button
                  className="shrink-0 hover:opacity-70 transition-opacity text-text-primary"
                  aria-label="Clear selected text"
                  onClick={onClearQuotedText}
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {strategyType && (
              <div className="py-2 flex items-start gap-2">
                <div className="inline-flex items-center gap-1 bg-[#1a1a1a] text-[#A772FF] px-2 py-1 rounded-md font-medium">
                  <span className="flex-1 text-sm">{t(strategyType)}</span>
                  <button
                    className="hover:opacity-70 transition-opacity ml-1 cursor-pointer"
                    aria-label="Remove function"
                    onClick={() => setStrategyType?.(undefined)}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}

            {!isDictating ? (
              // 正常输入模式
              <div
                className={cn(
                  'grid grid-cols-[auto_1fr_auto] transition-all duration-200',
                  !isExpanded
                    ? '[grid-template-areas:"leading_primary_trailing"]'
                    : '[grid-template-areas:"primary_primary_primary"_"leading_footer_trailing"]',
                )}
              >
                {/* Leading：模式图标 + 工具 */}
                <div className="[grid-area:leading] flex items-end gap-1">
                  {showProductIcon && (
                    <SearchProductIcon
                      isWorkspace={isWorkspace}
                      isStrategy={isStrategy}
                      agent={agent}
                      inChat={inChat}
                      mode={mode}
                    />
                  )}
                  {false && mode === 'alfa' && (
                    <SeacrhRange focusMode={focusMode} setFocusMode={setFocusMode} />
                  )}
                  {mode === 'brain' && <AgentVersion />}
                  {mode === 'strategy' && localStorage.getItem('__workspace__') && (
                    <StrategyTypes
                      onChange={(type) => {
                        setMessage('');
                        setShowExpand(false);
                        setStrategyType?.(type);
                      }}
                    />
                  )}
                  {false && (
                    <Attach
                      fileIds={fileIds}
                      setFileIds={setFileIds}
                      files={files}
                      setFiles={setFiles}
                      showText={false}
                    />
                  )}
                </div>

                {/* Primary：输入区 */}
                <div
                  className={cn(
                    '[grid-area:primary] flex items-center px-1.5',
                    isExpanded ? 'items-start pt-1' : '',
                  )}
                >
                  <TextareaAutosize
                    ref={inputRef}
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      if (e.target.value.trim()) {
                        setShowSuggestions(false);
                      }
                      if (!e.target.value.trim()) {
                        setShowExpand(false);
                      }
                    }}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onMouseDown={handleMouseDown}
                    onHeightChange={(h) => {
                      if (h > 30 && message.length > 0) {
                        setShowExpand(true);
                      }
                    }}
                    className={cn(
                      'bg-transparent placeholder:text-black/50 dark:placeholder:text-white/50 text-[16px] font-light text-text-primary resize-none outline-hidden w-full px-1.5 max-h-36 transition-all duration-300 ease-out',
                      {
                        // 'min-h-[48px]': floating || inChat,
                        // 'min-h-[60px]': !floating && !inChat,
                        'caret-[#a580df]': inChat,
                      },
                    )}
                    placeholder={
                      !isSmall
                        ? allQueries.length > 0
                          ? allQueries[placeholderIndex]
                          : t('Search anything...')
                        : t('Search anything...')
                    }
                    maxRows={message.length > 0 ? 6 : 1}
                    disabled={isCreating || loading}
                  />
                </div>

                {/* Trailing：单行无内容→语音，单行有内容或多行→发送 */}
                <div className="[grid-area:trailing] flex items-end">
                  {false && (
                    <button
                      type="button"
                      onClick={startDictation}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      aria-label="开始听写"
                    >
                      <DictationIcon className="w-5 h-5 text-text-secondary" />
                    </button>
                  )}
                  {/* // 发送 / 停止 按钮 */}
                  <button
                    disabled={disabled}
                    className={cn(
                      'h-[32px] w-[32px] -rotate-90 flex items-center justify-center transition duration-100 rounded-full p-2',
                      'disabled:text-[#BABABA] dark:disabled:text-[#757575]',
                      'disabled:bg-[#E9E9E9] dark:disabled:bg-[#202020]',
                      {
                        'dark:bg-[#FAFAFA] bg-[#121212] dark:text-[#000000] text-[#EFEFEF]':
                          !disabled,
                        'cursor-not-allowed': disabled,
                      },
                    )}
                  >
                    {isCreating || (loading && !currentTaskId) ? (
                      <Loader2 size={17} className="animate-spin text-text-tertiary" />
                    ) : loading && currentTaskId ? (
                      <Square
                        size={12}
                        className="fill-white stroke-white dark:fill-black dark:stroke-black"
                      />
                    ) : (
                      <ArrowRight size={17} strokeWidth={3} />
                    )}
                  </button>
                </div>

                {/* Footer：仅展开时渲染，可放置额外工具 */}
                {isExpanded && <div className="[grid-area:footer] min-h-[40px]" />}
              </div>
            ) : (
              // 听写模式
              <DictationMode
                isListening={isListening}
                transcript={transcript}
                interimTranscript={interimTranscript}
                audioLevel={audioLevel}
                barsConfig={barsConfigRef.current}
                onStop={stopDictation}
              />
            )}
          </div>
        </div>

        {/* 添加动画样式 */}
        <style jsx>{`
          @keyframes wave {
            0% {
              transform: scaleY(1);
            }
            100% {
              transform: scaleY(1.8);
            }
          }
          @keyframes pulse {
            0%,
            100% {
              opacity: 0.4;
            }
            50% {
              opacity: 1;
            }
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </form>
      {/* {!isSmall && <SearchLimitReached />} */}
    </div>
  );
};

export default EmptyChatMessageInput;

interface SearchProductIconProps {
  isWorkspace: boolean;
  isStrategy: boolean;
  agent?: AgentMode;
  inChat: boolean;
  mode: ChatMode;
}

function SearchProductIcon(props: SearchProductIconProps) {
  const { isWorkspace, mode, isStrategy, agent, inChat } = props;

  if (isWorkspace) {
    if (isStrategy) {
      return null;
    }
    return <DeepSearchSwitch enabled={agent === 'research'} />;
  }

  // if (inChat) return null;

  if (mode === 'research') {
    return (
      <span
        className={cn(
          'inline-flex size-[32px] transform items-center justify-center rounded-[14px] transition-transform duration-200',
        )}
      >
        <ResearchMenuIcon className={cn('h-5 w-5 transition-colors', 'text-[#666]')} />
      </span>
    );
  }

  // if (mode === 'predic') {
  //   return (
  //     <span className="inline-flex size-[32px] transform items-center justify-center rounded-[14px] transition-transform duration-200 text-[#666]">
  //       <PredicIcon size={20} />
  //     </span>
  //   );
  // }

  return (
    <span className="inline-flex size-[32px] transform items-center justify-center rounded-[14px] transition-transform duration-200">
      <AlfaGptMenuIcon
        className={cn('h-5 w-5 transition-colors', 'text-white')}
        // aiColor={'#523BCD'}
      />
    </span>
  );
}
